"use client";

import { fetchDoctors } from "../../authDoctor/redux/thunks/doctorsThunks.js";
import { fetchUsers } from "../../Profile/redux/thunks/userThunks";
import { fetchMedications } from "../../medications/redux/thunks/medicationsDatabaseThunks.js";
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { BellOutlined, MenuOutlined } from "@ant-design/icons";
import {
  Button,
  Divider,
  Dropdown,
  Empty,
  Image,
  Spin,
  Badge,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import useAuthSession from "../../authPatient/hooks/useAuthSession.js";
import { logoutUser } from "../../authPatient/redux/thunks/authThunks.js";
import { logout as logoutPatientLocal } from "../../authPatient/redux/slices/authSlice.js";
import { markSessionLogout } from "../../auth/sessionLogout.js";
import { selectAuthUser } from "../../authPatient/redux/selectors/authSelectors.js";
import { useUserInformation } from "../../Profile/hooks/useUserInformation.js";
import { useTheme } from "../../places/context/ThemeContext.jsx";
import LanguageSelector from "../../Landing/components/LanguageSelector/LanguageSelector.jsx";
import NavbarDateWeather from "./NavbarDateWeather.jsx";
import useSousProfiles from "../../sous-profile/hooks/useSousProfiles.js";

import useNotificationsSocket from "../../notifications/hooks/useNotificationsSocket.js";
import useNotifications from "../../notifications/hooks/useNotifications.js";
import { enablePushNotifications } from "../../notifications/pushNotifications.js";
import { getNotificationRoute } from "../../notifications/getNotificationRoute.js";
import { markReadLocal } from "../../notifications/redux/notificationsSlice.js";
import { getNotificationRequestedScopes } from "../../notifications/medicalAccessNotificationScopes.js";
import Cookies from "js-cookie";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  respondAccessRequest,
  revokeAccessRequest,
} from "../../notifications/redux/notificationsThunks.js";
import AccessRequestActions from "../../notifications/components/AccessRequestActions/AccessRequestActions.jsx";
import NotificationsLoadError from "../../notifications/components/NotificationsLoadError/NotificationsLoadError.jsx";
import { invalidateMedicalCapabilities } from "../../authorization/useMedicalCapabilities.js";

import FeedbackPopup from "./FeedbackPopup/FeedbackPopup.jsx";
import PrivacyPolicyPopup from "./PrivacyPolicyPopup/PrivacyPolicyPopup.jsx";
import SwitchToDoctorModal from "./switchModalOpen/switchModalOpen.jsx";

import {
  selectLatestNotifications,
  selectUnreadCount,
  selectNotificationsError,
  selectNotificationsStatus,
} from "../../notifications/redux/notificationsSelectors.js";

import { createSelector } from "@reduxjs/toolkit";
import { axiosDoctorRegister } from "../../api/axios.js";
import useProfileCompletion from "../useProfileCompletion.js";
import ROUTES, { DOCTOR_ROUTES } from "../../routes/patientRoutes/routePaths.js";

import "./Navbar.css";
import navbarSearchMutedIcon from "../../assets/icons/svg/navbar-search-muted.svg";

import { selectEffectiveUserId, selectPrincipalUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";

/* =======================
   Memoized Selectors
======================= */
const selectDoctorsList = createSelector(
  [(state) => state.doctor?.doctors],
  (doctors) => doctors || []
);

const selectPatientsList = createSelector(
  [(state) => state.user?.list, (state) => state.user?.usersById],
  (list, usersById) => {
    if (list && list.length) return list;
    return Object.values(usersById || {});
  }
);

const selectMedicationsListRaw = createSelector(
  [(state) => state.medicationsDatabase?.medications],
  (medications) => medications || []
);

const selectNotificationsItems = createSelector(
  [(state) => state.notifications],
  (notifications) => ({
    items: notifications?.items || [],
    unreadCount: notifications?.unreadCount || 0,
  })
);

/* =======================
   Icons
======================= */
const SubProfileSwitchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21 12C21 9.61305 20.0518 7.32387 18.364 5.63604C16.6761 3.94821 14.3869 3 12 3C9.48395 3.00947 7.06897 3.99122 5.26 5.74L3 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 3V8H8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 12C3 14.3869 3.94821 16.6761 5.63604 18.364C7.32387 20.0518 9.61305 21 12 21C14.516 20.9905 16.931 20.0088 18.74 18.26L21 16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 16H21V21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SubProfileIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

/* =======================
   Helpers
======================= */
const pickId = (u) =>
  u?.id ?? u?._id ?? u?.userId ?? u?.pgId ?? u?.uuid ?? null;

const normId = (v) => {
  if (v == null) return null;

  let s = String(v).trim();
  if (!s) return null;

  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("PAT_") || s.startsWith("pat_")) s = s.slice(4);

  s = s.trim();

  if (!s || ["nan", "undefined", "null"].includes(s.toLowerCase())) {
    return null;
  }

  return s;
};

const formatDateTime = (iso) => {
  if (!iso) return null;

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const dateStr = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const timeStr = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${dateStr} at ${timeStr}`;
};

const collectDoctorIds = (d) => {
  if (!d) return [];

  const raw = d?.raw || {};
  const user = d?.user || raw?.user || {};
  const doctor = d?.doctor || raw?.doctor || {};

  const allIds = [
    d?.id,
    d?._id,
    d?.userId,
    d?.pgId,
    d?.uuid,
    d?.doctorId,

    raw?.id,
    raw?._id,
    raw?.userId,
    raw?.pgId,
    raw?.uuid,
    raw?.doctorId,

    user?.id,
    user?._id,
    user?.userId,
    user?.pgId,
    user?.uuid,

    doctor?.id,
    doctor?._id,
    doctor?.userId,
    doctor?.pgId,
    doctor?.uuid,
    doctor?.doctorId,
  ];

  const normalized = allIds
    .filter((x) => x != null && x !== "")
    .map((x) => normId(x))
    .filter(Boolean);

  const numeric = normalized
    .map((x) => {
      const n = Number(x);
      return Number.isFinite(n) ? String(n) : null;
    })
    .filter(Boolean);

  return Array.from(new Set([...normalized, ...numeric]));
};

const extractDoctorName = (d) => {
  if (!d) return null;

  const raw = d?.raw || {};

  const candidates = [
    d?.fullName,
    raw?.fullName,
    d?.name,
    raw?.name,
    d?.displayName,
    raw?.displayName,
  ];

  for (const c of candidates) {
    if (c && typeof c === "string" && c.trim().length > 0) {
      return c.trim();
    }
  }

  const firstName = d?.firstName || raw?.firstName || d?.prenom || raw?.prenom || "";
  const lastName = d?.lastName || raw?.lastName || d?.nom || raw?.nom || "";
  const combined = `${firstName} ${lastName}`.trim();

  return combined.length > 0 ? combined : null;
};

const isPrescriptionNotif = (n) => {
  const type = String(n?.type || "").toUpperCase();
  const event = String(n?.event || "").toLowerCase();
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  const metaEvent = String(meta?.event || "").toLowerCase();

  return (
    type.includes("PRESCRIPTION") ||
    event.includes("prescription") ||
    metaEvent.includes("prescription")
  );
};

const isCommentNotif = (n) => {
  const type = String(n?.type || "").toUpperCase();
  const event = String(n?.event || "").toLowerCase();
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  const metaEvent = String(meta?.event || "").toLowerCase();

  return (
    type.includes("COMMENT") ||
    event.includes("comment") ||
    metaEvent.includes("comment")
  );
};

const isDoctorCommenter = (n) => {
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};

  return (
    n?.isDoctorCommenter === true ||
    meta?.isDoctorCommenter === true ||
    !!n?.doctorId ||
    !!meta?.doctorId
  );
};

const getEventLower = (n) => {
  const e1 = String(n?.event || "").toLowerCase();
  const e2 = String(n?.type || "").toLowerCase();
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  const e3 = String(meta?.event || "").toLowerCase();

  return `${e1} ${e2} ${e3}`.trim();
};

const isAppointmentNotif = (n) => {
  const ev = getEventLower(n);
  const type = String(n?.type || "").toUpperCase();

  return (
    type.includes("APPOINTMENT") ||
    type.includes("RENDEZVOUS") ||
    type.includes("RDV") ||
    ev.includes("rendezvous") ||
    ev.includes("rdv") ||
    ev.includes("appointment")
  );
};

const isCancelledAppointment = (n) => {
  const ev = getEventLower(n);
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  const cancelledBy = String(meta?.cancelledBy || "").toUpperCase();

  return (
    ev.includes("cancelled") ||
    cancelledBy === "PATIENT" ||
    cancelledBy === "DOCTOR"
  );
};

const isConfirmedAppointment = (n) => {
  const ev = getEventLower(n);

  return (
    (ev.includes("confirmed") || ev.includes("confirm")) &&
    !isCancelledAppointment(n)
  );
};

const isAccessReq = (n) => {
  const type = String(n?.type || "").toLowerCase();
  const event = String(n?.event || "").toLowerCase();
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  const metaEvent = String(meta?.event || "").toLowerCase();

  return (
    (type === "appointment" && event.includes("data_access")) ||
    event === "patient.data_access.requested" ||
    metaEvent === "patient.data_access.requested"
  );
};

const getAccessReqId = (n) => {
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  return n?.requestId || meta?.requestId || n?.id || null;
};

const getAccessReqPatientId = (n) => {
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  return n?.toUserId || n?.to || meta?.patientId || null;
};

const getAccessReqDoctorId = (n) => {
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  return n?.doctorId || meta?.doctorId || null;
};

const getAccessReqSubprofileId = (n) => {
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  const value = n?.subprofileId ?? meta?.subprofileId;
  const normalized = value == null ? "" : String(value).trim();
  return normalized || null;
};

const ACCESS_SCOPE_LABELS = {
  "medical_information:read": "Lire les informations médicales",
  "medical_information:create": "Ajouter des informations médicales",
  "medical_information:update": "Modifier les informations médicales",
  "medical_document:read": "Lire les documents médicaux",
  "medical_document:create": "Ajouter des documents médicaux",
  "medication:read": "Lire les traitements",
  "medication:create": "Créer un traitement",
  "medication:update": "Modifier les traitements",
  "medication:stop": "Arrêter les traitements",
};

const getSubProfileId = (sp) =>
  sp?.id ||
  sp?._id ||
  sp?.subprofile_id ||
  sp?.subProfileId ||
  sp?.profile?.id;

const getSubProfileName = (sp) => {
  const firstName =
    sp?.firstName || sp?.first_name || sp?.profile?.firstName || "";

  const lastName =
    sp?.lastName || sp?.last_name || sp?.profile?.lastName || "";

  return (
    sp?.fullName ||
    sp?.full_name ||
    sp?.name ||
    sp?.profile?.fullName ||
    `${firstName} ${lastName}`.trim() ||
    "User name"
  );
};

const getSubProfileSubtitle = (sp, t) => {
  const type = String(
    sp?.type || sp?.profileType || sp?.relationship || ""
  ).toLowerCase();

  const gender = String(sp?.gender || "").toLowerCase();

  if (type.includes("parent")) {
    return t("header.menu.parent", { defaultValue: "Parent" });
  }

  if (gender === "female") {
    return t("header.menu.daughter", { defaultValue: "Daughter" });
  }

  if (gender === "male") {
    return t("header.menu.son", { defaultValue: "Son" });
  }

  return t("header.menu.subProfile", { defaultValue: "Sub profile" });
};

const getSubProfileAvatar = (sp) =>
  sp?.avatar ||
  sp?.avatarUrl ||
  sp?.photo ||
  sp?.photoUrl ||
  sp?.profile?.avatar ||
  sp?.profile?.photo ||
  "";

/* =======================
   Main Component
======================= */
export default function Navbar({
  collapsed,
  toggleCollapsed,
  onCtaClick,
  isMobile,
  onMobileMenuToggle,
  customStyle,
  mode = "patient",
  isCyclePage = false,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").startsWith("ar");
  const { theme, toggleTheme, setTheme } = useTheme();

  const {
    auth,
    effectiveUser,
    effectiveUserId,
    actingSession,
    principalUser,
  } = useAuthSession({ auto: true, once: true });

  const principalUserId = useSelector(selectPrincipalUserId); //notif sous-profile

  const authUser = useSelector(selectAuthUser);
  const lastKnownUserRef = useRef(null);
  const currentUser = authUser ? principalUser || auth?.user || authUser : null;
  if (currentUser) lastKnownUserRef.current = currentUser;
  const user = authUser ? currentUser || lastKnownUserRef.current : null;

  const doctorsList = useSelector(selectDoctorsList);
  const patientsList = useSelector(selectPatientsList);
  const medicationsListRaw = useSelector(selectMedicationsListRaw);

  const medicationsList = Array.isArray(medicationsListRaw)
    ? medicationsListRaw
    : medicationsListRaw?.data || medicationsListRaw?.rows || [];

  const doctorsStatus = useSelector((state) => state.doctor.status);
  const usersStatus = useSelector((state) => state.user.status);
  const medsStatus = useSelector((state) => state.medicationsDatabase.status);

  useEffect(() => {
    if (usersStatus === "idle") dispatch(fetchUsers());
    if (medsStatus === "idle") {
      dispatch(fetchMedications({ page: 1, limit: 20 }));
    }
    if (doctorsStatus === "idle") dispatch(fetchDoctors());
  }, [dispatch, usersStatus, medsStatus, doctorsStatus]);

  const { avatarSrc: mainAvatarSrc } = useUserInformation(user);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [subProfilesOpen, setSubProfilesOpen] = useState(false);
  const [asyncNames, setAsyncNames] = useState({});
  const [loadingAccessReqs, setLoadingAccessReqs] = useState(new Set());

  const nameCacheRef = useRef(new Map());
  const pendingFetchesRef = useRef(new Set());

  const [searchValue, setSearchValue] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  // ✅ Modal Switch to Doctor Mode
  const [switchDoctorOpen, setSwitchDoctorOpen] = useState(false);

  const {
    items: availableSubProfiles = [],
    current: activeSubProfile,
    loading: subProfilesLoading,
    isActingAsSubprofile,
    handleSwitchProfile,
    handleRestoreProfile,
  } = useSousProfiles({ autoFetch: true });

  const isSubProfileSession =
    isActingAsSubprofile ||
    actingSession?.mode === "subprofile" ||
    !!actingSession?.actingAs;

  const subProfiles = useMemo(
    () =>
      isSubProfileSession
        ? activeSubProfile
          ? [activeSubProfile]
          : []
        : availableSubProfiles,
    [activeSubProfile, availableSubProfiles, isSubProfileSession]
  );

  const actingProfileName =
    actingSession?.profile?.name ||
    actingSession?.profile?.fullName ||
    `${actingSession?.profile?.firstName || ""} ${
      actingSession?.profile?.lastName || ""
    }`.trim() ||
    null;

  const actingProfileEmail =
    actingSession?.profile?.email ||
    actingSession?.profile?.contactEmail ||
    actingSession?.email ||
    "";

  const actingLabel = actingProfileEmail || actingProfileName;

  const unreadCount = useSelector(selectUnreadCount);
  const status = useSelector(selectNotificationsStatus);
  const notificationsError = useSelector(selectNotificationsError);
  const latestSelector = useMemo(() => selectLatestNotifications(10), []);
  const notifications = useSelector(latestSelector);

  // const userId = useMemo(() => {
  //   const id = effectiveUserId || pickId(user) || pickId(authUser);
  //   return id ? String(id) : null;
  // }, [effectiveUserId, user, authUser]);

  const userId = useMemo(() => {
  const id = principalUserId || pickId(user) || pickId(authUser);
  return id ? String(id) : null;
}, [principalUserId, user, authUser]); //notif sous-profile

  const {
    isLoading: notifLoading,
    refresh: refreshNotifications,
    markRead,
    markAllRead,
  } = useNotifications({
    pageSize: 20,
    latestCount: 10,
    enableWs: false,
    autoFetch: true,
    role: "patient",
    userId,
  });

  const { items: rtNotifications, unreadCount: rtUnreadCount } = useSelector(
    selectNotificationsItems
  );

  const {
    percent,
    missing,
    loading: completionLoading,
  } = useProfileCompletion(userId, { enabled: !!userId });

  const completionPercent = completionLoading ? 0 : percent ?? 0;

  const isProfileComplete =
    !completionLoading &&
    completionPercent >= 100 &&
    (missing?.length ?? 0) === 0;

  const safeCompletionPercent = Math.max(
    0,
    Math.min(100, Number(completionPercent) || 0)
  );

  const { handleNotificationClick } = useNotificationsSocket({
    enabled: false,
    roleOverride: "patient",
    userIdOverride: userId,
  });

  const routes = useMemo(() => {
    if (mode === "patient") {
      return {
        profile: ROUTES?.PROFILE || "/profile",
        settings: ROUTES?.SETTINGS || "/settings",
        login: ROUTES?.LOGIN || "/LoginPatient",
        drProfile: (id) => `/doctor/${id}/profile`,
      };
    }

    return {
      profile: "/doctorprofile",
      settings: "/settingsdoctor",
      login: "/LoginDoctor",
      drProfile: (id) => `/drprofile/${id}`,
    };
  }, [mode]);

  useEffect(() => {
    if (!userId) return;
    dispatch(fetchNotifications({ skip: 0, limit: 20, userId }));
  }, [dispatch, userId]);

  const logout = useCallback(async () => {
    markSessionLogout("patient");
    lastKnownUserRef.current = null;
    const patientToken =
      Cookies.get("accessToken") ||
      Cookies.get("auth_token") ||
      Cookies.get("token") ||
      localStorage.getItem("patientAccessToken") ||
      sessionStorage.getItem("patientAccessToken");
    dispatch(logoutUser({ token: patientToken }));
    dispatch(logoutPatientLocal());

    [
      "token",
      "accessToken",
      "id",
      "userId",
      "auth_token",
      "patientAccessToken",
    ].forEach((k) => {
      document.cookie = `${k}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      Cookies.remove(k);
      Cookies.remove(k, { path: "/", domain: ".mediwave.tn" });
    });

    if (typeof window !== "undefined") {
      localStorage.removeItem("patientAccessToken");
      sessionStorage.removeItem("patientAccessToken");
      localStorage.removeItem("userRole");
      if (sessionStorage.getItem("activeSessionRole") === "patient") {
        sessionStorage.removeItem("activeSessionRole");
      }
    }

    setTheme("light");
  }, [dispatch, setTheme]);

  const fetchDoctorNameFromApi = useCallback(async (doctorId) => {
    const normalizedId = normId(doctorId);
    if (!normalizedId) return null;

    const cacheKey = `doctor:${normalizedId}`;

    if (nameCacheRef.current.has(cacheKey)) {
      return nameCacheRef.current.get(cacheKey);
    }

    if (pendingFetchesRef.current.has(cacheKey)) return null;

    pendingFetchesRef.current.add(cacheKey);

    try {
      const res = await axiosDoctorRegister.get(
        `/${encodeURIComponent(normalizedId)}`,
        { withCredentials: true, timeout: 5000 }
      );

      const doctor = res.data;
      const name = extractDoctorName(doctor);

      if (name) nameCacheRef.current.set(cacheKey, name);

      return name || null;
    } catch {
      return null;
    } finally {
      pendingFetchesRef.current.delete(cacheKey);
    }
  }, []);

  const resolveDoctorNameById = useCallback(
    (fromUserId) => {
      const fidRaw = normId(fromUserId);
      if (!fidRaw) return null;

      const fidNum = String(Number(fidRaw));
      const wanted = new Set([fidRaw, fidNum].filter((x) => x && x !== "NaN"));

      const cacheKey = `doctor:${fidRaw}`;

      if (nameCacheRef.current.has(cacheKey)) {
        return nameCacheRef.current.get(cacheKey);
      }

      for (const doc of doctorsList || []) {
        const docIds = collectDoctorIds(doc);

        if (docIds.some((cid) => wanted.has(cid))) {
          const name = extractDoctorName(doc);

          if (name) {
            nameCacheRef.current.set(cacheKey, name);
            return name;
          }
        }
      }

      return null;
    },
    [doctorsList]
  );

  /* =======================
     Search suggestions (with dedup)
  ======================= */
  const searchSuggestions = useMemo(() => {
    if (!searchValue) return [];

    const q = searchValue.toLowerCase();

    // ---- Doctors ----
    const seenDoctors = new Set();

    const docs = (doctorsList || [])
      .filter((d) => {
        const fullName = `${d.firstName || ""} ${d.lastName || ""}`.toLowerCase();
        const email = (d.email || "").toLowerCase();
        const specialty = (d.specialty || d.speciality || "").toLowerCase();

        return (
          fullName.includes(q) ||
          email.includes(q) ||
          specialty.includes(q)
        );
      })
      .filter((d) => {
        // Dedup key: prefer normalized id, fallback to name+specialty
        const idKey =
          normId(d.id) ||
          normId(d._id) ||
          normId(d.userId) ||
          normId(d.doctorId);

        const fallbackKey = `${(d.firstName || "").toLowerCase()}-${(
          d.lastName || ""
        ).toLowerCase()}-${(d.specialty || d.speciality || "").toLowerCase()}`;

        const key = idKey || fallbackKey;

        if (seenDoctors.has(key)) return false;
        seenDoctors.add(key);
        return true;
      })
      .slice(0, 4)
      .map((d) => ({ type: "doctor", item: d }));

    // ---- Patients ----
    const seenPatients = new Set();

    const pats = (patientsList || [])
      .filter((p) => {
        const fullName = (
          p.fullName ||
          `${p.firstName || ""} ${p.lastName || ""}`
        ).toLowerCase();

        const email = (p.email || "").toLowerCase();

        return fullName.includes(q) || email.includes(q);
      })
      .filter((p) => {
        const idKey = normId(p.id) || normId(p._id) || normId(p.userId);

        const fallbackKey = (
          p.fullName || `${p.firstName || ""} ${p.lastName || ""}`
        )
          .trim()
          .toLowerCase();

        const key = idKey || fallbackKey;

        if (seenPatients.has(key)) return false;
        seenPatients.add(key);
        return true;
      })
      .slice(0, 3)
      .map((p) => ({ type: "patient", item: p }));

    // ---- Medications ----
    const seenMeds = new Set();

    const meds = (medicationsList || [])
      .filter((m) => {
        const name = (
          m.nom_commercial ||
          m.dci ||
          m.name ||
          m.denomination ||
          ""
        ).toLowerCase();

        return name.includes(q);
      })
      .filter((m) => {
        const idKey =
          normId(m._id) || normId(m.id) || normId(m.medicationId);

        const fallbackKey = (
          m.nom_commercial ||
          m.dci ||
          m.name ||
          m.denomination ||
          ""
        )
          .trim()
          .toLowerCase();

        const key = idKey || fallbackKey;

        if (seenMeds.has(key)) return false;
        seenMeds.add(key);
        return true;
      })
      .slice(0, 6)
      .map((m) => ({ type: "medication", item: m }));

    return [...docs, ...pats, ...meds].slice(0, 8);
  }, [doctorsList, patientsList, medicationsList, searchValue]);

  const handleSearchSelect = useCallback(
    (entry) => {
      if (!entry) return;

      const { type, item } = entry;

      if (type === "doctor") {
        const doctorId =
          normId(item?.id) ||
          normId(item?.doctorId) ||
          normId(item?.userId) ||
          normId(item?._id);

        if (
          !doctorId ||
          ["nan", "null", "undefined"].includes(doctorId.toLowerCase())
        ) {
          return;
        }

        navigate(DOCTOR_ROUTES.PROFILE_DOCTOR(doctorId));
      } else if (type === "patient") {
        const id = normId(item?.id) || normId(item?._id) || normId(item?.userId);

        if (!id || ["nan", "null", "undefined"].includes(id.toLowerCase())) {
          return;
        }

        navigate(`/patientprofile/${id}`);
      } else if (type === "medication") {
        const id =
          normId(item?._id) || normId(item?.id) || normId(item?.medicationId);

        if (!id || ["nan", "null", "undefined"].includes(id.toLowerCase())) {
          return;
        }

        navigate(`/medications/${id}`);
      }

      setSearchValue("");
      setSearchOpen(false);
    },
    [navigate]
  );

  const handleSearch = () => {
    if (!searchValue.trim()) return;
  };

  /* =======================
     Notification text
  ======================= */
  const formatNotifText = useCallback(
    (n) => {
      const type = String(n?.type || n?.event || "").toUpperCase();
      const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
      const notifId = String(n?.id || n?._id || Math.random());

      if (isCommentNotif(n)) {
        let commenterName =
          n?.fromUserName ||
          n?.commenterName ||
          meta?.fromUserName ||
          meta?.commenterName ||
          meta?.doctorName ||
          n?.doctorName ||
          null;

        if (!commenterName?.trim() || commenterName === "Unknown user") {
          commenterName = null;
        }

        if (!commenterName && asyncNames[notifId]) {
          const cached = asyncNames[notifId];

          if (cached !== "__loading__" && cached !== "__failed__") {
            commenterName = cached;
          }
        }

        const fromId =
          n?.fromUserId || n?.userId || meta?.fromUserId || meta?.userId;

        if (!commenterName && fromId) {
          commenterName = resolveDoctorNameById(fromId);
        }

        if (!commenterName && fromId && !asyncNames[notifId]) {
          setAsyncNames((prev) => ({ ...prev, [notifId]: "__loading__" }));

          fetchDoctorNameFromApi(fromId)
            .then((name) =>
              setAsyncNames((prev) => ({
                ...prev,
                [notifId]: name || "__failed__",
              }))
            )
            .catch(() =>
              setAsyncNames((prev) => ({
                ...prev,
                [notifId]: "__failed__",
              }))
            );
        }

        if (asyncNames[notifId] === "__loading__") {
          return t("header.notifications.loadingMessage");
        }

        if (
          !commenterName &&
          asyncNames[notifId] &&
          asyncNames[notifId] !== "__failed__"
        ) {
          commenterName = asyncNames[notifId];
        }

        const isDoctor = isDoctorCommenter(n);
        const prefix = isDoctor ? "DR. " : "";

        if (commenterName && commenterName !== "__failed__") {
          return t("header.notifications.commentAdded", {
            name: `${prefix}${commenterName}`,
          });
        }

        return t("header.notifications.newComment");
      }

      if (type.includes("MESSAGE") || type.includes("CHAT")) {
        let senderName =
          n?.fromUserName ||
          n?.senderName ||
          n?.senderFullName ||
          meta?.fromUserName ||
          meta?.senderName ||
          meta?.senderFullName ||
          meta?.doctorName ||
          meta?.patientName ||
          meta?.userName ||
          n?.doctorName ||
          n?.patientName ||
          null;

        if (!senderName?.trim() || senderName === "Unknown user") {
          senderName = null;
        }

        if (!senderName && asyncNames[notifId]) {
          const cached = asyncNames[notifId];

          if (cached !== "__loading__" && cached !== "__failed__") {
            senderName = cached;
          }
        }

        const fromId =
          n?.fromUserId ||
          n?.senderId ||
          meta?.fromUserId ||
          meta?.senderId ||
          meta?.doctorId ||
          n?.doctorId ||
          meta?.userId ||
          null;

        if (!senderName && fromId) {
          senderName = resolveDoctorNameById(fromId);
        }

        if (!senderName && fromId && !asyncNames[notifId]) {
          setAsyncNames((prev) => ({ ...prev, [notifId]: "__loading__" }));

          fetchDoctorNameFromApi(fromId)
            .then((name) =>
              setAsyncNames((prev) => ({
                ...prev,
                [notifId]: name || "__failed__",
              }))
            )
            .catch(() =>
              setAsyncNames((prev) => ({
                ...prev,
                [notifId]: "__failed__",
              }))
            );
        }

        if (asyncNames[notifId] === "__loading__") {
          return t("header.notifications.loadingMessage");
        }

        if (
          !senderName &&
          asyncNames[notifId] &&
          asyncNames[notifId] !== "__failed__"
        ) {
          senderName = asyncNames[notifId];
        }

        if (senderName && senderName !== "__failed__") {
          return t("header.notifications.messageFrom", { name: senderName });
        }

        return t("header.notifications.newMessage");
      }

      if (isPrescriptionNotif(n)) {
        const doctorName =
          meta?.doctorName ||
          n?.doctorName ||
          resolveDoctorNameById(meta?.doctorId || n?.doctorId) ||
          t("header.notifications.aDoctor");

        return t("header.notifications.prescriptionAddedFor", {
          name: doctorName,
        });
      }

      if (isAppointmentNotif(n)) {
        const when = formatDateTime(meta?.startTime || n?.startTime || n?.date);

        const doctorName =
          meta?.doctorName ||
          n?.doctorName ||
          resolveDoctorNameById(meta?.doctorId || n?.doctorId) ||
          t("header.notifications.aDoctor");

        const doctorDisplayName = doctorName && !String(doctorName).toLowerCase().startsWith("dr.")
          ? `Dr. ${doctorName}`
          : doctorName;

        const cancelled = isCancelledAppointment(n);
        const confirmed = isConfirmedAppointment(n);

        if (cancelled) {
          return when
            ? t("header.notifications.appointmentCancelledWithWhen", {
                name: doctorDisplayName,
                when,
              })
            : t("header.notifications.appointmentCancelledWith", {
                name: doctorDisplayName,
              });
        }

        if (confirmed) {
          return when
            ? t("header.notifications.appointmentConfirmedWithWhen", {
                name: doctorDisplayName,
                when,
              })
            : t("header.notifications.appointmentConfirmedWith", {
                name: doctorDisplayName,
              });
        }

        return when
          ? t("header.notifications.appointmentUpdatedWithWhen", {
              name: doctorDisplayName,
              when,
            }) || `Appointment update with ${doctorDisplayName} on ${when}`
          : t("header.notifications.appointmentUpdatedWith", {
              name: doctorDisplayName,
            }) || `Appointment update with ${doctorDisplayName}`;
      }

      if (isAccessReq(n)) {
        const accessMeta = n?.metadata || n?.data || n?._raw?.metadata || {};
        const doctorName =
          n?.doctorName ||
          accessMeta?.doctorName ||
          resolveDoctorNameById(accessMeta?.doctorId || n?.doctorId) ||
          t("header.notifications.aDoctor");
        const doctorDisplayName = doctorName && !String(doctorName).toLowerCase().startsWith("dr.")
          ? `Dr. ${doctorName}`
          : doctorName;

        return (
          t("header.notifications.accessRequestFrom", { name: doctorDisplayName }) ||
          `${doctorDisplayName} requested access to your profile`
        );
        return scopeCount
          ? `${base} · ${scopeCount} autorisation${scopeCount > 1 ? "s" : ""}`
          : base;
      }

      return n?.title || n?.message || t("header.notifications.default");
    },
    [asyncNames, fetchDoctorNameFromApi, resolveDoctorNameById, t]
  );

  /* =======================
     FIX message notification click for PATIENT navbar
  ======================= */
  const getMessageSenderInfo = useCallback(
    (n) => {
      const meta = n?.metadata || n?.data || n?._raw?.metadata || {};

      const rawType = String(n?.type || n?.event || "").toUpperCase();
      const rawEvent = String(meta?.event || n?.event || "").toLowerCase();

      const isMessage =
        rawType.includes("MESSAGE") ||
        rawType.includes("CHAT") ||
        rawEvent.includes("message") ||
        rawEvent.includes("chat");

      if (!isMessage) return null;

      const senderId =
        normId(n?.fromUserId) ||
        normId(n?.senderId) ||
        normId(n?.doctorId) ||
        normId(meta?.fromUserId) ||
        normId(meta?.senderId) ||
        normId(meta?.doctorId) ||
        normId(meta?.userId) ||
        null;

      if (!senderId) return null;

      let senderName =
        n?.fromUserName ||
        n?.senderName ||
        n?.senderFullName ||
        n?.doctorName ||
        meta?.fromUserName ||
        meta?.senderName ||
        meta?.senderFullName ||
        meta?.doctorName ||
        meta?.userName ||
        meta?.from?.name ||
        meta?.from?.fullName ||
        meta?.sender?.name ||
        meta?.sender?.fullName ||
        resolveDoctorNameById(senderId) ||
        asyncNames[String(n?.id || n?._id || "")] ||
        null;

      if (
        !senderName ||
        String(senderName).trim().toLowerCase() === "unknown user" ||
        String(senderName).trim().toLowerCase() === "unknown" ||
        senderName === "__loading__" ||
        senderName === "__failed__"
      ) {
        senderName = "Doctor";
      }

      const selectedContact = {
        id: senderId,
        _id: senderId,
        userId: senderId,
        peerId: senderId,
        doctorId: senderId,

        fullName: senderName,
        name: senderName,
        displayName: senderName,
        doctorName: senderName,

        role: "doctor",
        type: "doctor",

        meta: {
          type: "doctor",
          role: "doctor",
          doctorId: senderId,
          registerId: senderId,
          displayName: senderName,
          fullName: senderName,
          name: senderName,
        },
      };

      return {
        senderId,
        senderName,
        selectedContact,
        messageId: meta?.messageId || n?.messageId || null,
      };
    },
    [resolveDoctorNameById, asyncNames]
  );

  const onClickNotifItem = useCallback(
    async (n) => {
      try {
        const id = String(n?.id || n?._id || "").trim();

        if (id && !n?.read) {
          dispatch(markReadLocal(id));
          await dispatch(markNotificationRead({ id })).unwrap();
        }
      } catch (error) {
        console.error("[NOTIFICATIONS] Failed to mark notification read", error);
      }

      const meta = n?.metadata || n?.data || n?._raw?.metadata || {}; //switch vers subprofile lors du clique sur ntif de confirmation de rdv 
      const isConfirmedAppointmentNotif =
        isAppointmentNotif(n) && isConfirmedAppointment(n);

      const subprofileId =
        meta?.subprofileId ||
        n?.subprofileId ||
        meta?.subProfileId ||
        n?.subProfileId ||
        null;

      // if (isConfirmedAppointmentNotif && subprofileId) {
      //   try {
      //     const result = await handleSwitchProfile(String(subprofileId));

      //     if (result?.ok) {
      //       setNotifOpen(false);

      //       window.dispatchEvent(
      //         new CustomEvent("actingSessionChanged", {
      //           detail: {
      //             mode: "subprofile",
      //             subprofileId: String(subprofileId),
      //           },
      //         })
      //       );

      //       navigate(ROUTES?.APPOINTMENTS || "/appointments");

      //       return;
      //     }
      //   } catch (error) {
      //     console.error(
      //       "[NOTIFICATION] Failed to switch to appointment subprofile:",
      //       error
      //     );
      //   }
      // }

    if (isConfirmedAppointmentNotif && subprofileId) {
  try {
    const result = await handleSwitchProfile(String(subprofileId));

    if (!result?.ok) {
      console.error(
        "[NOTIFICATION] Failed to switch to subprofile:",
        result
      );
      return;
    }

    setNotifOpen(false);

    window.dispatchEvent(
      new CustomEvent("actingSessionChanged", {
        detail: {
          mode: "subprofile",
          subprofileId: String(subprofileId),
        },
      })
    );

    // Switch vers le subprofile puis ouvrir ses rendez-vous
    navigate(ROUTES?.APPOINTMENTS || "/appointments");

    return;
  } catch (error) {
    console.error(
      "[NOTIFICATION] Failed to switch to appointment subprofile:",
      error
    );
  }
}

      if (isAccessReq(n)) return;

      const messageInfo = getMessageSenderInfo(n);

      if (messageInfo) {
        const { senderId, senderName, selectedContact, messageId } = messageInfo;

        sessionStorage.setItem(
          "pendingSelectedContact",
          JSON.stringify(selectedContact)
        );

        sessionStorage.setItem("autoSelectContactId", String(senderId));
        sessionStorage.setItem("autoSelectContactName", senderName);

        const targetRoute = getNotificationRoute(n, "patient");

        setNotifOpen(false);

        if (!targetRoute) return;
        navigate(targetRoute, {
          state: {
            selectedContact,
            autoOpen: true,
            fromNotif: true,
            fromUserId: senderId,
            doctorId: senderId,
            doctorName: senderName,
            messageId,
          },
        });

        return;
      }

      if (isCommentNotif(n)) {
        setNotifOpen(false);
        await handleNotificationClick(n);
        return;
      }

      if (isPrescriptionNotif(n)) {
        setNotifOpen(false);

        navigate(ROUTES?.MEDICINES || "/medicines", {
          state: {
            fromNotif: true,
            notif: n,
          },
        });

        return;
      }

      if (typeof handleNotificationClick === "function") {
        await handleNotificationClick(n);
      } else {
        const type = String(n?.type || n?.event || "").toUpperCase();

        if (type.includes("APPOINTMENT")) {
          navigate(ROUTES?.APPOINTMENTS || "/appointments");
        } else if (n?.link) {
          navigate(n.link);
        } else {
          navigate("/notifications");
        }
      }

      setNotifOpen(false);
    },
    [dispatch, navigate, getMessageSenderInfo, handleNotificationClick, handleSwitchProfile,]
  );

  const handleAccessRequestResponse = useCallback(
    async (notif, status) => {
      const rid = getAccessReqId(notif);
      const patientId = getAccessReqPatientId(notif) || userId;
      const subprofileId =
        notif?.metadata?.subprofileId ||
        notif?.data?.subprofileId ||
        notif?.subprofileId ||
        null;

      if (!rid || !patientId) return;

      setLoadingAccessReqs((prev) => new Set([...prev, rid]));

      try {
        const scopes =
          status === "accepted"
            ? getNotificationRequestedScopes(notif)
            : undefined;
        if (status === "accepted" && !scopes) return;
        await dispatch(
          respondAccessRequest({ requestId: rid, patientId, status, scopes, subprofileId })
        ).unwrap();

        const id = String(notif?.id || notif?._id || "").trim();

        if (id) {
          dispatch(markNotificationRead({ id })).catch(() => {});
        }
      } catch (error) {
        message.error(
          typeof error === "string"
            ? error
            : error?.message || "La réponse à la demande a échoué.",
        );
      } finally {
        setLoadingAccessReqs((prev) => {
          const next = new Set(prev);
          next.delete(rid);
          return next;
        });
      }
    },
    [dispatch, userId]
  );

  const revokeAcceptedAccess = useCallback(
    async (notif) => {
      const rid = getAccessReqId(notif);
      const patientId = getAccessReqPatientId(notif) || userId;
      const doctorId = getAccessReqDoctorId(notif);
      const subprofileId = getAccessReqSubprofileId(notif);
      if (!rid || !patientId) return;

      setLoadingAccessReqs((prev) => new Set([...prev, rid]));
      try {
        const result = await dispatch(
          revokeAccessRequest({ requestId: rid, subprofileId }),
        ).unwrap();
        invalidateMedicalCapabilities(patientId, doctorId, [
          userId,
          effectiveUserId,
        ]);
        if (result?.alreadyResolved) {
          message.info("Cet accès était déjà inactif.");
        } else {
          message.success("Accès du médecin révoqué.");
        }
      } catch (error) {
        message.error(
          typeof error === "string"
            ? error
            : error?.message || "La révocation de l’accès a échoué.",
        );
        throw error;
      } finally {
        setLoadingAccessReqs((prev) => {
          const next = new Set(prev);
          next.delete(rid);
          return next;
        });
      }
    },
    [dispatch, userId],
  );

  const displayName =
    user?.name ||
    user?.profile?.name ||
    user?.fullName ||
    (user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : null) ||
    user?.firstName ||
    user?.lastName ||
    t("header.defaults.userName");

  const userInitials =
    `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() ||
    "?";

  const avatarSrc = mainAvatarSrc;

  // ✅ Ce patient a-t-il un compte doctor lié (créé via le flow
  // "Switch to Patient Mode" côté NavbarDoctor) ? On réutilise le même
  // flag localStorage posé lors de la création du compte patient.
  // N'affiche l'option QUE côté patient, jamais côté doctor.
  const hasLinkedDoctorAccount =
    mode === "patient" && user?.email
      ? localStorage.getItem(`patientProfileCreated:${user.email}`) === "true"
      : false;

  const onSwitchSubProfile = useCallback(
    async (sp) => {
      const subprofileId = getSubProfileId(sp);

      if (!subprofileId) {
        return;
      }

      const result = await handleSwitchProfile(subprofileId);

      if (result?.ok) {
        setDropdownOpen(false);
        setSubProfilesOpen(false);

        window.dispatchEvent(
          new CustomEvent("actingSessionChanged", {
            detail: { mode: "subprofile", subprofileId },
          })
        );

        window.location.reload();
      }
    },
    [handleSwitchProfile]
  );

  const onRestoreMainProfile = useCallback(async () => {
    const result = await handleRestoreProfile();

    if (result?.ok) {
      setDropdownOpen(false);
      setSubProfilesOpen(false);

      window.dispatchEvent(
        new CustomEvent("actingSessionChanged", {
          detail: { mode: "main" },
        })
      );

      window.location.reload();
    }
  }, [handleRestoreProfile]);

  /* =======================
     Profile dropdown overlay
  ======================= */
  const profileDropdownOverlay = (
    <div className="navbar__profile-dropdown" dir={isRTL ? "rtl" : "ltr"}>
      {!isSubProfileSession && !isProfileComplete && !completionLoading && (
        <div
          className="navbar__profile-dropdown__completion-banner"
          onClick={() => {
            navigate(ROUTES?.PROFILE_EDIT_GENERAL || "/profile/edit/general");
            setDropdownOpen(false);
          }}
        >
          <span className="navbar__profile-dropdown__completion-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#f97316"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </span>

          <span className="navbar__profile-dropdown__completion-text">
            {t("header.profileProgress.completedPercent", {
              percent: completionPercent,
            }) || `Your profile is only ${completionPercent}% complete`}
          </span>
        </div>
      )}

      <div className="navbar__profile-dropdown__menu">
        <div
          className="navbar__profile-dropdown__item"
          onClick={() => {
            navigate(ROUTES?.PROFILE_EDIT_GENERAL || "/profile/edit/general");
            setDropdownOpen(false);
          }}
        >
          <span className="navbar__profile-dropdown__item-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="7.5" r="3.2" />
              <path d="M4 19.5c0-3.6 3.6-6.5 8-6.5s8 2.9 8 6.5" />
            </svg>
          </span>
          <span>{t("profileCard.editProfile", "Edit profile")}</span>
        </div>

        <div
          className="navbar__profile-dropdown__item"
          onClick={() => {
            navigate(routes.settings);
            setDropdownOpen(false);
          }}
        >
          <span className="navbar__profile-dropdown__item-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06A2 2 0 0 1 20.8 6.4l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </span>
          <span>{t("header.menu.settings") || "Settings"}</span>
        </div>

        {/* ✅ Switch to Doctor Mode — visible seulement si ce patient a un compte doctor lié */}
        {hasLinkedDoctorAccount && (
          <div
            className="navbar__profile-dropdown__item"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDropdownOpen(false);
              setTimeout(() => setSwitchDoctorOpen(true), 0);
            }}
          >
            <span className="navbar__profile-dropdown__item-icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 1l4 4-4 4" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <path d="M7 23l-4-4 4-4" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
            </span>
            <span>
              {t("header.menu.switchToDoctor", {
                defaultValue: "Switch to Doctor Mode",
              })}
            </span>
          </div>
        )}

        <div
          className="navbar__profile-dropdown__item"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDropdownOpen(false);
            setTimeout(() => setPrivacyOpen(true), 0);
          }}
        >
          <span className="navbar__profile-dropdown__item-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </span>
          <span>{t("header.menu.policyPrivacy") || "policy & privacy"}</span>
        </div>

        <div
          className="navbar__profile-dropdown__item"
          onClick={() => {
            setFeedbackOpen(true);
            setDropdownOpen(false);
          }}
        >
          <span className="navbar__profile-dropdown__item-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <span>
            {t("header.menu.feedbackReclamations") ||
              "feedback & reclamations"}
          </span>
        </div>

        <div
          className="navbar__profile-dropdown__item navbar__profile-dropdown__item--subprofiles"
          onClick={() => setSubProfilesOpen((v) => !v)}
        >
          <span className="navbar__profile-dropdown__item-icon">
            <SubProfileIcon />
          </span>
          <span>{t("header.menu.subProfiles") || "Sub profiles"}</span>

          <span
            className={`navbar__profile-dropdown__chevron ${
              subProfilesOpen ? "navbar__profile-dropdown__chevron--open" : ""
            }`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>

        {subProfilesOpen && (
          <div className="navbar__subprofiles-panel">
            {isSubProfileSession && (
              <button
                type="button"
                className="navbar__subprofile-restore"
                onClick={(e) => {
                  e.stopPropagation();
                  onRestoreMainProfile();
                }}
              >
                <span className="navbar__subprofile-restore-icon">↩</span>
                <span>
                  {t("header.menu.restoreMainProfile", {
                    defaultValue: "Back to main profile",
                  })}
                </span>
              </button>
            )}

            {subProfilesLoading ? (
              <div className="navbar__subprofiles-loading">
                <Spin size="small" />
                <span>
                  {t("header.menu.loadingSubProfiles", {
                    defaultValue: "Loading sub-profiles...",
                  })}
                </span>
              </div>
            ) : subProfiles.length === 0 ? (
              <div className="navbar__subprofiles-empty">
                <span>
                  {t("header.menu.noSubProfiles", {
                    defaultValue: "No sub-profiles yet",
                  })}
                </span>

                {/* ✅ CHANGED: navigate to /family-tree instead of /profile/sub-profiles/create */}
                <button
                  type="button"
                  className="navbar__subprofiles-add-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/family-tree");
                    setDropdownOpen(false);
                    setSubProfilesOpen(false);
                  }}
                >
                  {t("header.menu.addSubProfile", {
                    defaultValue: "+ Add sub-profile",
                  })}
                </button>
              </div>
            ) : (
              subProfiles.map((sp) => {
                const id = getSubProfileId(sp);
                const name = getSubProfileName(sp);
                const subtitle = getSubProfileSubtitle(sp, t);
                const avatar = getSubProfileAvatar(sp);
                const initial = name?.trim()?.[0]?.toUpperCase() || "?";

                return (
                  <button
                    type="button"
                    key={id || name}
                    className="navbar__subprofile-item"
                    disabled={isSubProfileSession}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isSubProfileSession) {
                        onSwitchSubProfile(sp);
                      }
                    }}
                    title={
                      isSubProfileSession
                        ? t("header.menu.activeSubProfile", {
                            defaultValue: "Current sub-profile",
                          })
                        : t("header.menu.switchToSubProfile", {
                            defaultValue: "Switch to this sub-profile",
                          })
                    }
                  >
                    <div className="navbar__subprofile-avatar">
                      {avatar ? (
                        <img src={avatar} alt={name} />
                      ) : (
                        <span>{initial}</span>
                      )}
                    </div>

                    <div className="navbar__subprofile-info">
                      <div className="navbar__subprofile-name-row">
                        <span className="navbar__subprofile-name">{name}</span>

                        {!isSubProfileSession && (
                          <span
                            className="navbar__subprofile-switch-icon"
                            aria-hidden="true"
                          >
                            <SubProfileSwitchIcon />
                          </span>
                        )}
                      </div>

                      <span className="navbar__subprofile-role">{subtitle}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}

        <div className="navbar__profile-dropdown__divider" />

        <div
          className="navbar__profile-dropdown__item navbar__profile-dropdown__item--logout"
          onClick={() => {
            void logout();
            navigate("/LoginPatient", { replace: true });
            setDropdownOpen(false);
          }}
        >
          <span className="navbar__profile-dropdown__item-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </span>
          <span>{t("header.menu.logout") || "Logout"}</span>
        </div>
      </div>
    </div>
  );

  /* =======================
     Notification overlay
  ======================= */
  const activeNotifications = mode === "patient" ? notifications : rtNotifications;
  const activeUnreadCount = mode === "patient" ? unreadCount : rtUnreadCount;
  const activeStatus =
    mode === "patient" ? status : notifLoading ? "loading" : "idle";
  const activeError = mode === "patient" ? notificationsError : null;

  const notifOverlay = (
    <div className="navbar__notif-dropdown" dir={isRTL ? "rtl" : "ltr"}>
      <div className="navbar__notif-header">
        <span className="navbar__notif-title">
          {t("header.notifications.title")}
        </span>

        <Button
          type="link"
          size="small"
          onClick={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            try {
              await enablePushNotifications(userId);
              message.success("Browser notifications enabled.");
            } catch (error) {
              message.info(error?.message || "Could not enable browser notifications.");
            }
          }}
          disabled={!userId}
        >
          Enable browser alerts
        </Button>

        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            mode === "patient"
              ? dispatch(markAllNotificationsRead())
              : markAllRead();
          }}
          className="navbar__notif-seeall"
          disabled={activeUnreadCount === 0}
        >
          {t("header.notifications.markAllAsRead")}
        </Button>
      </div>

      <Divider style={{ margin: "8px 0" }} />

      {activeStatus === "loading" ? (
        <div style={{ padding: 12, textAlign: "center" }}>
          <Spin size="small" />
        </div>
      ) : activeStatus === "failed" ? (
        <NotificationsLoadError
          error={activeError}
          onRetry={refreshNotifications}
        />
      ) : !activeNotifications?.length ? (
        <div style={{ padding: 12 }}>
          <Empty description={t("header.notifications.empty")} />
        </div>
      ) : (
        <div className="navbar__notif-list">
          {activeNotifications.map((n, idx) => {
            const id = n?.id || n?._id || `${idx}`;
            const isUnread = !n?.read;

            const text =
              mode === "patient"
                ? formatNotifText(n)
                : n?.title || n?.message || t("header.notifications.default");

            const date = n?.createdAt || n?.date;
            const isAccessRequest = mode === "patient" && isAccessReq(n);
            const rid = getAccessReqId(n);
            const isLoadingReq = loadingAccessReqs.has(rid);
            const resolvedScopes = isAccessRequest
              ? getNotificationRequestedScopes(n)
              : [];
            const requestedScopes = resolvedScopes ?? [];
            const scopesUnavailable =
              isAccessRequest && resolvedScopes === null;
            const accessRequestStatus = String(
              n?.metadata?.status ||
                n?.data?.status ||
                n?._raw?.metadata?.status ||
                "pending",
            ).toLowerCase();

            return (
              <div
                key={String(id)}
                className={`navbar__notif-item ${
                  isUnread ? "navbar__notif-item--unread" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  mode === "patient"
                    ? onClickNotifItem(n)
                    : (id && markRead(id), setNotifOpen(false));
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.currentTarget.click();
                  }
                }}
              >
                <div className="navbar__notif-dot" />

                <div className="navbar__notif-content">
                  <div className="navbar__notif-text">{text}</div>

                  {requestedScopes.length > 0 && (
                    <div className="navbar__notif-permissions">
                      <strong>Autorisations demandées :</strong>
                      <ul>
                        {requestedScopes.map((scope) => (
                          <li key={scope}>{ACCESS_SCOPE_LABELS[scope] || scope}</li>
                        ))}
                      </ul>
                      <small>Durée maximale : 30 jours. Révocable à tout moment.</small>
                    </div>
                  )}

                  {scopesUnavailable && (
                    <div className="navbar__notif-permissions" role="alert">
                      <strong>Scopes indisponibles.</strong>
                      <small>Demandez au médecin une nouvelle autorisation.</small>
                    </div>
                  )}

                  {date && (
                    <div className="navbar__notif-date">
                      {new Date(date).toLocaleString("en-GB")}
                    </div>
                  )}

                  {isAccessRequest && (
                    <AccessRequestActions
                      status={accessRequestStatus}
                      loading={isLoadingReq}
                      disabled={scopesUnavailable}
                      onAccept={() =>
                        handleAccessRequestResponse(n, "accepted")
                      }
                      onReject={() =>
                        handleAccessRequestResponse(n, "rejected")
                      }
                      onRevoke={() => revokeAcceptedAccess(n)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (!user) {
    // Only show the spinner while the initial session check is in flight.
    // Once it has resolved (success or failure) and there is still no user,
    // stop spinning so the header doesn't hang forever on a failed refresh.
    if (auth?.isLoading && !auth?.isError) {
      return (
        <div className="navbar navbar--loading" dir={isRTL ? "rtl" : "ltr"}>
          <Spin size="small" />
        </div>
      );
    }
    return (
      <div className="navbar navbar--loading" dir={isRTL ? "rtl" : "ltr"}>
        <span className="navbar__user-name">{t("header.defaults.userName")}</span>
      </div>
    );
  }

  return (
    <div
      className={`navbar-wrapper ${
        isMobile ? "navbar-wrapper--mobile" : ""
      } ${customStyle?.className || ""}`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <nav
        className={`navbar navbar--unified${
          isCyclePage ? " navbar--cycle" : ""
        }`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="navbar__left">
          {isMobile ? (
            <button
              className="navbar__mobile-menu-btn"
              onClick={onMobileMenuToggle}
              aria-label="Toggle menu"
            >
              <MenuOutlined className="navbar__mobile-menu-icon" />
            </button>
          ) : (
            <div className="navbar__user-identity">
              <div
                className="navbar__user-avatar-wrapper"
                onClick={() => {
                  const id = userId || pickId(user);

                  navigate(
                    id
                      ? `/profile/${id}`
                      : ROUTES?.PROFILE_EDIT_GENERAL || "/profile/edit/general"
                  );
                }}
              >
                {avatarSrc ? (
                  <Image
                    src={avatarSrc}
                    width={40}
                    height={40}
                    preview={false}
                    className="navbar__user-avatar-img"
                    fallback="https://cdn-icons-png.flaticon.com/512/149/149071.png"
                    style={{ borderRadius: "50%", objectFit: "cover" }}
                  />
                ) : (
                  <div className="navbar__user-avatar-placeholder">
                    {userInitials}
                  </div>
                )}
              </div>

              <div className="navbar__user-info">
                <span className="navbar__user-name">{displayName}</span>
              </div>
            </div>
          )}
        </div>

        <div className="navbar__right">
          {!isMobile && (
            <div className="navbar__search-container navbar__search-container--right">
              <input
                type="text"
                placeholder={t("navbar.searchPlaceholder")}
                className="navbar__search-input"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />

              <button className="navbar__search-button" onClick={handleSearch}>
                <img
                  src={navbarSearchMutedIcon}
                  alt=""
                  aria-hidden="true"
                  width="17"
                  height="17"
                />
              </button>

              {searchOpen && searchSuggestions.length > 0 && (
                <div className="navbar__search-dropdown">
                  {searchSuggestions.map((entry, i) => {
                    const { type, item } = entry;

                    return (
                      <div
                        key={`${type}:${item.id || item._id || i}`}
                        className="navbar__search-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSearchSelect(entry);
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <div>
                            {type === "doctor" && (
                              <>
                                <strong>
                                  {item.fullName ||
                                    `${item.firstName || ""} ${
                                      item.lastName || ""
                                    }`.trim()}
                                </strong>

                                {item.specialty && (
                                  <div className="navbar__search-sub">
                                    {item.specialty}
                                  </div>
                                )}

                                {item.email && (
                                  <div className="navbar__search-sub">
                                    {item.email}
                                  </div>
                                )}
                              </>
                            )}

                            {type === "patient" && (
                              <>
                                <strong>
                                  {item.fullName ||
                                    `${item.firstName || ""} ${
                                      item.lastName || ""
                                    }`.trim()}
                                </strong>

                                {item.email && (
                                  <div className="navbar__search-sub">
                                    {item.email}
                                  </div>
                                )}

                                {item.phone && (
                                  <div className="navbar__search-sub">
                                    {item.phone}
                                  </div>
                                )}
                              </>
                            )}

                            {type === "medication" && (
                              <>
                                <strong>
                                  {item.nom_commercial ||
                                    item.dci ||
                                    item.name ||
                                    item.denomination}
                                </strong>

                                {item.forme && (
                                  <div className="navbar__search-sub">
                                    {item.forme}
                                  </div>
                                )}

                                {item.laboratoire && (
                                  <div className="navbar__search-sub">
                                    {item.laboratoire}
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          <div
                            style={{
                              marginLeft: 12,
                              fontSize: 12,
                              color: "#6b7280",
                            }}
                          >
                            {type}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchOpen && searchValue && searchSuggestions.length === 0 && (
                <div className="navbar__search-dropdown">
                  <Empty description={t("submenu.search.noUserFound")} />
                </div>
              )}
            </div>
          )}

          <NavbarDateWeather />
          <LanguageSelector />

          <div
            className="navbar__icon-button navbar__icon-button--theme"
            onClick={toggleTheme}
            role="button"
            tabIndex={0}
            aria-label={
              theme === "dark"
                ? t("submenu.theme.enableLight")
                : t("submenu.theme.enableDark")
            }
          >
            {theme === "dark" ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M21 12.79A9 9 0 0 1 12.79 3 7 7 0 1 0 21 12.79Z" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="5" />
                <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                  <line x1="12" y1="2" x2="12" y2="5" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="4.22" y1="4.22" x2="6.34" y2="6.34" />
                  <line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
                  <line x1="2" y1="12" x2="5" y2="12" />
                  <line x1="19" y1="12" x2="22" y2="12" />
                  <line x1="4.22" y1="19.78" x2="6.34" y2="17.66" />
                  <line x1="17.66" y1="6.34" x2="19.78" y2="4.22" />
                </g>
              </svg>
            )}
          </div>

          <Dropdown
            overlay={notifOverlay}
            placement="bottomRight"
            trigger={["click"]}
            open={notifOpen}
            onOpenChange={(v) => {
              setNotifOpen(v);
              if (v) setDropdownOpen(false);
            }}
            overlayClassName="navbar__notif-overlay"
            overlayStyle={{ padding: 0, position: "fixed", zIndex: 9999 }}
            getPopupContainer={() => document.body}
            destroyPopupOnHide={false}
          >
            <div
              className="navbar__icon-button navbar__icon-button--notification"
              role="button"
              aria-label={t("header.notifications.aria")}
            >
              <Badge count={activeUnreadCount} overflowCount={99} size="small">
                <BellOutlined style={{ fontSize: 20, color: "#939597" }} />
              </Badge>
            </div>
          </Dropdown>

          <Dropdown
            overlay={profileDropdownOverlay}
            placement="bottomRight"
            trigger={["click"]}
            open={dropdownOpen}
            onOpenChange={(v) => {
              setDropdownOpen(v);

              if (!v) setSubProfilesOpen(false);
              if (v) setNotifOpen(false);
            }}
            overlayClassName="navbar__profile-dropdown-overlay"
            overlayStyle={{ padding: 0, position: "fixed", zIndex: 9999 }}
            getPopupContainer={() => document.body}
          >
            <div className="navbar__profile-trigger" role="button" tabIndex={0}>
              <div
                className="navbar__profile-progress-avatar"
                style={{
                  "--profile-progress-angle": `${
                    safeCompletionPercent * 3.6
                  }deg`,
                }}
              >
                <div className="navbar__profile-progress-avatar-inner">
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt={displayName}
                      className="navbar__profile-progress-avatar-img"
                    />
                  ) : (
                    <div className="navbar__profile-progress-avatar-placeholder">
                      {userInitials}
                    </div>
                  )}
                </div>
              </div>

              <span
                className={`navbar__profile-trigger-chevron ${
                  dropdownOpen ? "navbar__profile-trigger-chevron--open" : ""
                }`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
          </Dropdown>
        </div>
      </nav>

      {isSubProfileSession && (
        <div className="navbar__acting-subprofile-bar">
          <div className="navbar__acting-subprofile-inner">
            <span className="navbar__acting-subprofile-text">
              {t("header.subprofileActing.actingAs", {
                name: actingLabel,
                defaultValue: `Acting as ${actingLabel}`,
              })}
            </span>

            <button
              type="button"
              className="navbar__acting-subprofile-back"
              onClick={onRestoreMainProfile}
            >
              {t("header.subprofileActing.backToAccount", {
                defaultValue: "Back to your account",
              })}
            </button>
          </div>
        </div>
      )}

      <PrivacyPolicyPopup
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
      />

      <FeedbackPopup
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        user={user}
      />

      {/* ✅ Modal Switch to Doctor Mode */}
      <SwitchToDoctorModal
        open={switchDoctorOpen}
        onClose={() => setSwitchDoctorOpen(false)}
        patient={user}
      />
    </div>
  );
}
